import fuzzy from 'fuzzaldrin-plus'
import React from 'react'
import { MenuItem, Position, ButtonProps, MenuDivider } from '@blueprintjs/core'
import { Select2 } from '@blueprintjs/select'
import { Button, Flex, HighlightedText, IconButton } from '@ui'
import ClearableSelectWrapper from '@ui/selects/ClearableSelectWrapper'

export type MAPPING_VALUE =
  | 'external_id'
  | 'name'
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'phone'
  | 'avatar_url'
  | 'initial_assignment'
  | 'end_date'
  | 'start_date'
  | 'department_name'
  | 'team_name'
  | 'location'
  | 'reporting_to'
  | 'title'
  | 'employment_type'
  | 'slack_handle'
  | 'cost_center_name'
  | 'sub_cost_center_name'
  | 'badge'
  | 'workplace_group_name'
  | 'Create Extra Field'
  | 'false'
  | 'neighborhood_name'
  | 'is_remote'
  | 'no_desk_needed'

export interface IMapping {
  value: MAPPING_VALUE
  label: string
  helperText?: string
}

interface ISeparator {
  label: string
}

export const MAPPINGS: Array<IMapping | ISeparator> = [
  { label: 'REQUIRED' },
  {
    value: 'department_name',
    label: 'Department',
    helperText: '(e.g. Engineering)',
  },
  { value: 'email', label: 'Email' },
  {
    value: 'employment_type',
    label: 'Employment Type',
    helperText: '(e.g. full time, intern)',
  },
  { value: 'name', label: 'Full Name' },
  { label: 'OPTIONAL' },
  { value: 'avatar_url', label: 'Avatar' },
  { value: 'badge', label: 'Badge' },
  { value: 'cost_center_name', label: 'Cost Center' },
  { value: 'external_id', label: 'Employee ID' },
  { value: 'workplace_group_name', label: 'Employee Workplace Group' },
  { value: 'end_date', label: 'Employment End Date' },
  { value: 'start_date', label: 'Employment Start Date' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  {
    value: 'location',
    label: 'Location',
    helperText: '(e.g. SF, London)',
  },
  { value: 'phone', label: 'Phone' },
  { value: 'reporting_to', label: 'Reporting to' },
  { value: 'slack_handle', label: 'Slack Handle' },
  { value: 'sub_cost_center_name', label: 'Sub-Cost Center' },
  {
    value: 'team_name',
    label: 'Team',
    helperText: '(e.g. Software Engineering)',
  },
  { value: 'title', label: 'Title' },
  { value: 'initial_assignment', label: 'Workstation number' },
  { value: 'neighborhood_name', label: 'Neighborhood' },
  { value: 'is_remote', label: 'Remote' },
  { value: 'no_desk_needed', label: 'No Desk Needed' },
  { label: 'OTHER' },
  { value: 'Create Extra Field', label: 'Extra field' },
  { value: 'false', label: 'Skip column' },
]

interface IMappingSelectProps {
  buttonProps?: ButtonProps
  value?: MAPPING_VALUE | null
  onChange: (value: MAPPING_VALUE | null) => void
}

const MappingSelect = Select2<IMapping | ISeparator>

export default function MappingSelectWrapper({
  value,
  onChange,
  buttonProps,
}: IMappingSelectProps) {
  const _value = MAPPINGS.find((mapping) => 'value' in mapping && mapping.value === value)

  return (
    <ClearableSelectWrapper>
      <MappingSelect
        scrollToActiveItem
        resetOnClose
        popoverProps={{
          minimal: true,
          position: Position.BOTTOM,
        }}
        noResults={<MenuItem disabled text="No results" />}
        items={MAPPINGS}
        itemRenderer={(mapping, { handleClick, modifiers, query }) =>
          'value' in mapping ? (
            <MenuItem
              key={mapping.value}
              active={mapping.value === value || modifiers.active}
              disabled={modifiers.disabled}
              onClick={handleClick}
              text={
                <Flex align="baseline">
                  <HighlightedText text={mapping.label} query={query} />
                  {mapping.helperText && (
                    <span style={{ color: 'var(--grey1)', paddingLeft: 5 }}>
                      {mapping.helperText}
                    </span>
                  )}
                </Flex>
              }
            />
          ) : (
            <MenuDivider key={mapping.label} title={mapping.label} />
          )
        }
        onItemSelect={(newMapping) => 'value' in newMapping && onChange(newMapping.value)}
        itemListPredicate={(query, items) =>
          query
            ? fuzzy.filter(
                items.filter((i) => 'value' in i),
                query,
                { key: 'label' }
              )
            : items
        }
      >
        <Button {...buttonProps} text={_value?.label || '- Select -'} />
      </MappingSelect>
      {value && (
        <IconButton small minimal iconName="times" tooltip="Clear" onClick={() => onChange(null)} />
      )}
    </ClearableSelectWrapper>
  )
}
